import DocumentTextIcon from '@heroicons/react/outline/esm/DocumentTextIcon.js'
import { clsx } from 'clsx'

import {
  Emoji,
  Image,
  SearchEntity,
  SearchEntityType,
} from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import { IconEmoji } from '../common/components/IconEmoji.js'
import { T } from '../i18n/components/T.js'
import { SearchSubtitle } from './SearchSubtitle.js'
import { getSearchItemLink } from './utils.js'

export const SearchRowPreview = ({
  hit,
  entityType,
}: {
  hit: SearchEntity
  entityType: SearchEntityType
}) => {
  switch (entityType) {
    case SearchEntityType.member:
      return <Avatar size="md" src={(hit?.media as Image)?.url} />
    case SearchEntityType.External:
      return <DocumentTextIcon className="w-10 h-10 text-content-subdued" />
    case SearchEntityType.space:
    case SearchEntityType.post:
      {
        const media = (hit?.in?.image as any)?.__typename
          ? hit?.in?.image
          : hit?.media
        if ((media as any)?.__typename === 'Emoji') {
          return (
            <div className="w-10 h-10 flex items-center justify-center">
              <IconEmoji text={(media as Emoji)?.text} size="lg" />
            </div>
          )
        }

        if ((media as any)?.__typename === 'Image') {
          return <Avatar rounded="md" size="md" src={(media as Image)?.url} />
        }
      }
      return null
    default:
      return null
  }
}

export const SearchRow = ({
  hit,
  entityType,
  spacing = 'md',
}: {
  hit: SearchEntity
  entityType: SearchEntityType
  spacing?: string
}) => {
  const { Link } = useRouter()

  // The space for the post is deleted
  if (entityType === SearchEntityType.post && !hit?.in) {
    return null
  }

  return (
    <Link
      href={getSearchItemLink(hit)}
      target={entityType === SearchEntityType.External ? '_blank' : '_self'}
      className={clsx(
        'flex items-center space-s-3',
        spacing === 'md' && 'px-6 py-5',
        spacing === 'sm' && 'px-2 py-2',
      )}
    >
      <div className="flex-shrink-0">
        <SearchRowPreview hit={hit} entityType={entityType} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-content">{hit.title}</p>
        <p className="text-content-subdued truncate">
          {!!hit && <SearchSubtitle hit={hit} />}
        </p>
      </div>
    </Link>
  )
}

export const SearchRowPage = ({ search, link, spacing = 'md' }) => {
  const { Link } = useRouter()

  if (!search) {
    return null
  }

  return (
    <Link
      href={link}
      className={clsx(
        'flex items-center space-s-3',
        spacing === 'md' && 'px-6 py-5',
        spacing === 'sm' && 'px-2 py-2',
      )}
    >
      <div className="flex-1 min-w-0">
        <span className="text-content">{search}</span>
        <span className="text-content-subdued truncate">
          {' — '}
          <T
            id="Search.Hints.SearchEverything"
            defaultMessage="Search posts, {MEMBERS} and more"
          />
        </span>
      </div>
    </Link>
  )
}
